import { useEffect, useId } from "react";

import { observer } from "mobx-react-lite";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { ChatsTab } from "@/components/doc/left-sidebar/chats-tab/ChatsTab";
import { DocumentTree } from "@/components/doc/left-sidebar/documents/DocumentTree";
import { DocumentTreeControls } from "@/components/doc/left-sidebar/documents/DocumentTreeControls";
import { NavInstructions } from "@/components/doc/left-sidebar/NavInstructions";
import { TeamDropdown } from "@/components/doc/left-sidebar/team-dropdown/TeamDropdown";
import { NotificationsPopover } from "@/components/doc/left-sidebar/top-menu/NotificationsPopover";
import { SidebarTabs } from "@/components/doc/left-sidebar/top-menu/SidebarTabs";
import { SearchList } from "@/components/search/SearchList";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { useLockBodyScroll } from "@/utils/useLockBodyScroll";

const BottomMenu = observer(function BottomMenu() {
  const { auth } = useStore();

  if (!auth.isLoggedIn) {
    return null;
  }

  return (
    <Sidebar.Group className="fixed z-10 bottom-6 left-1/2 -translate-x-1/2 w-fit p-1 gap-5">
      <SidebarTabs />
      <NotificationsPopover />
      <TeamDropdown avatarOnly withArrow />
    </Sidebar.Group>
  );
});

const DocsTab = observer(function DocsTab() {
  const { auth, search } = useStore();
  const t = useTranslations("documentsSidebar");
  const instructionsId = useId();

  if (!auth.isLoggedIn) {
    return null;
  }

  return (
    <Sidebar.Group className="p-0 flex-col">
      <DocumentTreeControls />
      {search.isOpen && search.searchQuery ? (
        <SearchList nested />
      ) : (
        <nav
          aria-label={t("documentTreePanel")}
          className="m-1.25 mt-1.5"
          aria-describedby={instructionsId}
        >
          <NavInstructions id={instructionsId} />
          <DocumentTree />
        </nav>
      )}
    </Sidebar.Group>
  );
});

export const MobileDocs = observer(function MobileDocs() {
  const { docId: currentDocId } = useParams<{ [key: string]: string }>();
  const { documentTree, view } = useStore();

  useLockBodyScroll();

  useEffect(() => {
    if (currentDocId) {
      documentTree.setCurrentDoc(currentDocId);
    }
  }, [documentTree, currentDocId]);

  return (
    <div className="sidebar-focus-ring hidden sm:flex flex-col fixed z-100 overflow-auto top-0 left-0 w-full h-dvh p-2.5 pb-24 bg-document-bg">
      {view.leftSidebarTab === "chats" ? <ChatsTab /> : <DocsTab />}
      <BottomMenu />
    </div>
  );
});
